/**
 * Global ring buffer of recent errors. Initialized once in main.tsx.
 * Captures window 'error' events, 'unhandledrejection', and console.error calls.
 */

export interface BufferedError {
  message: string
  stack: string
  timestamp: number
}

const MAX = 20
let buffer: BufferedError[] = []
let installed = false

function push(message: string, stack: string) {
  buffer.push({ message: String(message ?? ''), stack: String(stack ?? ''), timestamp: Date.now() })
  if (buffer.length > MAX) buffer = buffer.slice(-MAX)
}

function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>()
  try {
    return JSON.stringify(value, (_k, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]'
        seen.add(v)
      }
      return v
    })
  } catch {
    return String(value)
  }
}

export function installErrorBuffer() {
  if (installed) return
  installed = true

  window.addEventListener('error', (e) => {
    push(e.message || 'Error', e.error?.stack || '')
  })

  window.addEventListener('unhandledrejection', (e) => {
    const reason = (e as PromiseRejectionEvent).reason
    push(
      typeof reason === 'string' ? reason : safeStringify(reason),
      reason?.stack || ''
    )
  })

  const origError = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    push(args.map(a => (typeof a === 'string' ? a : safeStringify(a))).join(' '), '')
    origError(...args)
  }
}

export function getRecentErrors(): BufferedError[] {
  return buffer.slice()
}

export function _resetForTests() {
  buffer = []
  installed = false
}
